import {
  Box,
  Flex,
  InjectedModalProps,
  LinkExternal,
  Message,
  Skeleton,
  Text,
  CopyAddress,
  FlexGap,
  useTooltip,
  TooltipText,
  InfoFilledIcon,
  ScanLink,
  Button,
  WalletFilledIcon,
  Colors,
  CopyButton,
} from '@pancakeswap/uikit'
import { NINEINCH_ADDRESS } from 'config/constants/nineinch'
import { WNATIVE } from '@pancakeswap/sdk'
import { ChainId } from '@pancakeswap/chains'
import { FetchStatus } from 'config/constants/types'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTranslation } from '@pancakeswap/localization'
import useAuth from 'hooks/useAuth'
import useNativeCurrency from 'hooks/useNativeCurrency'
import useTokenBalance, { useBSCCakeBalance } from 'hooks/useTokenBalance'
import { ChainLogo } from 'components/Logo/ChainLogo'

import { getBlockExploreLink, getBlockExploreName } from 'utils'
import { getFullDisplayBalance, formatBigInt } from '@pancakeswap/utils/formatBalance'
import { useBalance } from 'wagmi'
import { useDomainNameForAddress } from 'hooks/useDomain'
import { isMobile } from 'react-device-detect'
import { useState } from 'react'
import InternalLink from 'components/Links'
import { SUPPORT_BUY_CRYPTO } from 'config/constants/supportChains'
import EBox from 'components/EBox'
import styled from 'styled-components'

const COLORS = {
  ETH: '#627EEA',
  BNB: '#14151A',
}

const createAbbreviatedAddress = (address: string | undefined) => {
  if (!address) return ''
  const firstPart = address.slice(0, 7)
  const lastPart = address.slice(-6)
  return `${firstPart}...${lastPart}`
}

interface WalletInfoProps {
  hasLowNativeBalance: boolean
  switchView: (newIndex: number) => void
  onDismiss: InjectedModalProps['onDismiss']
}

const WalletContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 12px;
  width: 100%;
  position: relative;
  padding-top: 20px; // Reduced top padding
`

const MenuIconWrapper = styled.div<{ borderColor: keyof Colors }>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  border-color: ${({ theme, borderColor }) => theme.colors[borderColor]};
  border-radius: 50%;
  border-style: solid;
  border-width: 2px;
  display: flex;
  height: 60px;
  justify-content: center;
  width: 60px;
  position: absolute;
  top: -10px; // Moved icon much closer to the address
  left: 50%;
  transform: translateX(-50%);
  z-index: 102;
`;

const AddressContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 8px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  margin-top: 40px;
`

const AddressText = styled(Text)`
  font-size: 20px; // Decreased font size
  font-weight: 600; // Semi-bold weight

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 16px;
  }
`

const WalletInfo: React.FC<WalletInfoProps> = ({ hasLowNativeBalance, onDismiss }) => {
  const { t } = useTranslation()
  const { account, chainId, chain } = useActiveWeb3React()
  const { domainName } = useDomainNameForAddress(account)
  const isBSC = chainId === ChainId.BSC
  const nativeBalance = useBalance({ address: account, enabled: !isBSC })
  const native = useNativeCurrency()
  const wNativeToken = !isBSC ? WNATIVE[chainId] : null
  const { balance: wNativeBalance, fetchStatus: wNativeFetchStatus } = useTokenBalance(wNativeToken?.address)
  const { balance: wNineinchBalance, fetchStatus: wNineinchFetchStatus } = useTokenBalance(NINEINCH_ADDRESS)
  const [mobileTooltipShow, setMobileTooltipShow] = useState(false)
  const { logout } = useAuth()

  const handleLogout = () => {
    onDismiss?.()
    logout()
  }

  const showNativeEntryPoint = Number(nativeBalance?.data?.value) === 0 && SUPPORT_BUY_CRYPTO.includes(chainId)

  return (
    <WalletContainer>
      <MenuIconWrapper borderColor="primary">
        <WalletFilledIcon color="primary" width="40px" height="40px" /> {/* Further decreased icon size */}
      </MenuIconWrapper>
      <AddressContainer>
        <AddressText bold>
          {createAbbreviatedAddress(account)}
        </AddressText>
        <CopyButton width="18px" height="18px" text={account} tooltipMessage={"Copied"} /> {/* Slightly reduced size */}
      </AddressContainer>
      {/* {hasLowNativeBalance && SUPPORT_BUY_CRYPTO.includes(chainId) && (
        <Message variant="warning" mb="24px">
          <Box>
            <Text fontWeight="bold">
              {t('%currency% Balance Low', {
                currency: native.symbol,
              })}
            </Text>
            <InternalLink href="/buy-crypto" onClick={() => onDismiss?.()}>
              <Text color="primary">
                {t('You need %currency% for transaction fees.', {
                  currency: native.symbol,
                })}
              </Text>
            </InternalLink>
          </Box>
        </Message>
      )} */}
      {/* {!isBSC && chain && (
        <Box mb="12px">
          <Flex justifyContent="space-between" alignItems="center" mb="8px">
            <Flex py="2px">
              <ChainLogo chainId={chain.id} />
              <Text color="white">{chain.name}</Text>
            </Flex>
            <LinkExternal href={getBlockExploreLink(account, 'address', chainId)}>
              {getBlockExploreName(chainId)}
            </LinkExternal>
          </Flex>
          <Flex alignItems="center" justifyContent="space-between">
            <Text color="textSubtle" fontSize="12px">
              {native.symbol} {t('Balance')}
            </Text>
            {!nativeBalance.isFetched ? (
              <Skeleton height="22px" width="60px" />
            ) : (
              <Flex>
                <Text
                  color={showNativeEntryPoint ? 'warning' : 'text'}
                  fontWeight={showNativeEntryPoint ? 'bold' : 'normal'}
                  fontSize="12px"
                >
                  {formatBigInt(nativeBalance?.data?.value ?? 0n, 6)}
                </Text>
              </Flex>
            )}
          </Flex>
          {wNineinchBalance && wNineinchBalance.gt(0) && (
            <Flex alignItems="center" justifyContent="space-between">
              <Text color="textSubtle" fontSize="12px">
                NINEINCH {t('Balance')}
              </Text>
              {wNineinchFetchStatus !== FetchStatus.Fetched ? (
                <Skeleton height="22px" width="60px" />
              ) : (
                <Text fontSize="12px">{getFullDisplayBalance(wNineinchBalance, 18, 6)}</Text>
              )}
            </Flex>
          )}
        </Box>
      )} */}
      {/* <Flex justifyContent="center">
        <Button onClick={handleLogout}>Disconnect</Button>
      </Flex> */}
    </WalletContainer>
  )
}

export default WalletInfo
